import { Fragment, useEffect, useState } from "react";
import {
    Backdrop, Box, Button, Card, CardActions, CardContent, CardMedia,
    CircularProgress, Container, FormControl, Grid, InputLabel, Select,
    TextField
} from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckAttr from "../../../../utils/CheckAttr";
import { DataRef } from "../../../../types/DataRef";
import axios, { AxiosInstance } from "axios";
import Cookies from "universal-cookie";
import { SnackBarAlert } from "../../../element/SnackBarAlert";
import { AlertType } from "../../../../types/AlertType";
import { downloadFile } from "../../../../utils/utils";
import { yearsToDisplayCommission } from "../MainCommission";
import { AllMounth } from "../../../../constants/Constants";

interface Commerciaux {
    id: number;
    prenom: string;
    name: string;
    email: string;
    pays: DataRef[];
}

interface EXTRACTION {
    userId?: number;
    pays?: number;
    client?: number;
    client_libelle?: string;
    operation?: number;
    operation_libelle?: string;
    date_debut?: string;
    date_fin?: string;
    annee?: number;
    mois?: number;
}

interface InputValidation {
    date_debut: boolean;
    date_fin: boolean;
    annee: boolean;
}

const initialData: EXTRACTION = {
    userId: undefined,
    pays: undefined,
    client: undefined,
    client_libelle: undefined,
    operation: undefined,
    operation_libelle: undefined,
    date_debut: undefined,
    date_fin: undefined,
    annee: undefined,
    mois: undefined
}

const resetInpuValidation: InputValidation = {
    date_debut: false,
    date_fin: false,
    annee: false
};

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

enum EXTRACTION_TYPE {
    CONSOLIDATION = "CONSOLIDATION",
    DETAIL_PAR_ARTICLE = "DETAIL_PAR_ARTICLE"
};

type ExtractionType = EXTRACTION_TYPE.CONSOLIDATION | EXTRACTION_TYPE.DETAIL_PAR_ARTICLE;

const ExtractionCommission = () => {
    const cookies = new Cookies();
    const token: string = cookies.get("jwt");

    const http: AxiosInstance = axios.create({
        baseURL: urlBaseApi,
        // timeout: 1000,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    const [loader, setLoader] = useState<boolean>(false);

    const [alertMessage, setAlerteMessage] = useState<string>('');
    const [openSnack, setOpenSnack] = useState<boolean>(false);
    const [alertType, setAlertType] = useState<AlertType>("success");

    const [dataCommerciaux, setDataCommerciaux] = useState<Commerciaux[]>([]);
    const [dataPays, setDataPays] = useState<DataRef[]>([]);
    const [dataClients, setDataClients] = useState<DataRef[]>([]);
    const [dataOperations, setDataOperations] = useState<DataRef[]>([]);

    const [extractionType, setExtractionTypes] = useState<ExtractionType>(EXTRACTION_TYPE.CONSOLIDATION);
    const [extractionDatas, setExtractionDatas] = useState<EXTRACTION>(initialData);

    const [errorInput, setErrorInput] = useState<InputValidation>(resetInpuValidation);

    const retrieveDatasCommerciaux = async () => {
        await http.get('commission/get-commerciaux-with-his-pays')
            .then(res => {
                Array.isArray(res.data) && setDataCommerciaux(res.data);
            }).catch(error => {
                console.log(error);
            });
    };

    const retrieveDatasPays = async () => {
        await http.get('pays/find/all')
            .then(res => {
                Array.isArray(res.data) && setDataPays(res.data);
            }).catch(error => {
                console.log(error);
            });
    };

    const retrieveDatasClients = async () => {
        await http.post('commission/get-clients', { commercial: extractionDatas?.userId })
            .then(res => {
                Array.isArray(res.data) && setDataClients(res.data);
            }).catch(error => {
                console.log(error);
            });
    };

    const retrieveDatasOperations = async () => {
        await http.post('commission/get-operations', {
            commercial: CheckAttr(extractionDatas?.userId) ? extractionDatas?.userId : undefined,
            client: CheckAttr(extractionDatas?.client) ? extractionDatas?.client : undefined
        }).then(res => {
            Array.isArray(res.data) && setDataOperations(res.data);
        }).catch(error => {
            console.log(error);
        });
    };

    useEffect(() => {
        retrieveDatasCommerciaux();
    }, []);

    useEffect(() => {
        if (CheckAttr(extractionDatas?.userId)) {
            const pays = dataCommerciaux?.find(c => c.id === Number(extractionDatas?.userId))?.pays;
            pays && setDataPays(pays);
        } else {
            retrieveDatasPays();
        }

        retrieveDatasClients();
    }, [extractionDatas?.userId]);

    useEffect(() => {
        retrieveDatasOperations();
    }, [extractionDatas?.userId, extractionDatas?.client]);

    useEffect(() => {
        if (extractionType === EXTRACTION_TYPE.CONSOLIDATION) {
            setExtractionDatas({
                ...extractionDatas,
                annee: undefined,
                mois: undefined
            });
        }

        if (extractionType === EXTRACTION_TYPE.DETAIL_PAR_ARTICLE) {
            setExtractionDatas({
                ...extractionDatas,
                date_debut: undefined,
                date_fin: undefined
            });
        }
    }, [extractionType]);

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'client':
                if (CheckAttr(value)) {
                    const libelle = dataClients?.find(c => c.id === Number(value))?.libelle as string;
                    setExtractionDatas({
                        ...extractionDatas,
                        client: value,
                        client_libelle: libelle
                    });
                } else {
                    setExtractionDatas({
                        ...extractionDatas,
                        client: value,
                        client_libelle: undefined
                    });
                }
                break;
            case 'operation':
                if (CheckAttr(value)) {
                    const libelle = dataOperations?.find(o => o.id === Number(value))?.libelle as string;
                    setExtractionDatas({
                        ...extractionDatas,
                        operation: value,
                        operation_libelle: libelle
                    });
                } else {
                    setExtractionDatas({
                        ...extractionDatas,
                        operation: value,
                        operation_libelle: undefined
                    });
                }
                break;

            default:
                setExtractionDatas({
                    ...extractionDatas,
                    [name]: value
                });
                break;
        }
    };

    const isValidate = (): boolean => {
        // Reinitialise les variables des validations
        setErrorInput(resetInpuValidation);

        if (extractionType === EXTRACTION_TYPE.CONSOLIDATION) {
            /**
             * Verification si date debut ou date fin est vide
             */
            if (extractionDatas?.date_debut && extractionDatas?.date_fin) {
                // Verification si date debut est supérieur au date fin
                const dateDeb = new Date(extractionDatas?.date_debut);
                const dateFin = new Date(extractionDatas?.date_fin);
                if (dateDeb.getTime() > dateFin.getTime()) {
                    setErrorInput({
                        ...resetInpuValidation,
                        date_debut: true,
                        date_fin: true
                    });

                    setAlerteMessage("Désolé! La date fin doit être supèrieur ou égal à la date debut !");
                    setAlertType("warning");
                    setOpenSnack(true);

                    return false;
                }
            } else {
                if (!CheckAttr(extractionDatas?.date_debut)) {
                    setErrorInput((prev: InputValidation) => ({ ...prev, date_debut: true }));
                }

                if (!CheckAttr(extractionDatas?.date_fin)) {
                    setErrorInput((prev: InputValidation) => ({ ...prev, date_fin: true }));
                }

                setAlerteMessage("Merci de remplir les champs en rouge SVP!");
                setAlertType("warning");
                setOpenSnack(true);

                return false;
            }
        }

        if (extractionType === EXTRACTION_TYPE.DETAIL_PAR_ARTICLE) {
            if (!CheckAttr(extractionDatas?.annee)) {
                setErrorInput((prev: InputValidation) => ({ ...prev, annee: true }));

                setAlerteMessage("Merci de remplir les champs en rouge SVP!");
                setAlertType("warning");
                setOpenSnack(true);

                return false;
            }
        }

        return true;
    }

    const handleExtract = () => {
        if (isValidate()) {
            setLoader(true);

            const url = (extractionType === EXTRACTION_TYPE.CONSOLIDATION) ? "commission/extract-by-consolidation" : "commission/extract-by-article-details";

            http.post(url, extractionDatas)
                .then(res => {
                    // On télécharge le fichier
                    const { success, filename } = res.data;
                    success && downloadFile(filename as string);

                    setAlerteMessage("Extraction efféctuée avec succès !");
                    setAlertType("success");
                    setOpenSnack(true);
                })
                .catch((error) => {
                    console.log(error);
                    setAlertType("error");
                    setOpenSnack(true);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    };

    const handleCloseAlert = () => {
        setOpenSnack(false)
    };

    const AllMounthCorriger = Number(extractionDatas?.annee) ===  new Date().getFullYear() ? AllMounth?.filter((mounth) => mounth?.id <= new Date().getMonth() + 1) : AllMounth;

    const stackAlert: JSX.Element = SnackBarAlert(openSnack, handleCloseAlert, alertType, alertMessage);

    return (
        <>
            <Container maxWidth="md" sx={{ mt: 10 }}>
                <Card sx={{ maxWidth: 845 }}>
                    <CardMedia
                        component="img"
                        image="/images/extraction_commission.png"
                        alt="green iguana"
                        sx={{ display: { xs: 'none', sm: 'flex' }, height: 140 }}
                    />
                    <CardContent>
                        <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }}>
                            <Grid item xs={6} sm={6} md={6}>
                                <FormControl
                                    variant="outlined"
                                    sx={{ marginTop: 1 }}
                                    fullWidth
                                >
                                    <InputLabel
                                        htmlFor="userId"
                                        color="warning"
                                        shrink={CheckAttr(extractionDatas?.userId)}
                                    >
                                        Commerciaux
                                    </InputLabel>
                                    <Select
                                        id="userId"
                                        native
                                        color="warning"
                                        value={extractionDatas?.userId}
                                        onChange={handleChange}
                                        label="Commerciaux"
                                        inputProps={{
                                            name: "userId",
                                        }}
                                    >
                                        <option value=""></option>
                                        {dataCommerciaux.map((commercial) => (
                                            <option key={commercial?.id} value={commercial?.id}>
                                                {commercial?.prenom} {commercial?.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={6} md={6}>
                                <FormControl
                                    variant="outlined"
                                    sx={{ marginTop: 1 }}
                                    fullWidth
                                >
                                    <InputLabel
                                        htmlFor="pays"
                                        color="warning"
                                        shrink={CheckAttr(extractionDatas?.pays)}
                                    >
                                        Pays
                                    </InputLabel>
                                    <Select
                                        id="pays"
                                        native
                                        color="warning"
                                        value={extractionDatas?.pays}
                                        onChange={handleChange}
                                        label="Pays"
                                        inputProps={{
                                            name: "pays",
                                        }}
                                    >
                                        <option value=""></option>
                                        {dataPays.map((pays) => (
                                            <option key={pays?.id} value={pays?.id}>
                                                {pays?.libelle}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={6} md={6}>
                                <FormControl
                                    variant="outlined"
                                    sx={{ marginTop: 1 }}
                                    fullWidth
                                >
                                    <InputLabel
                                        htmlFor="client"
                                        color="warning"
                                        shrink={CheckAttr(extractionDatas?.client)}
                                    >
                                        Client
                                    </InputLabel>
                                    <Select
                                        id="client"
                                        native
                                        color="warning"
                                        value={extractionDatas?.client}
                                        onChange={handleChange}
                                        label="Client"
                                        inputProps={{
                                            name: "client",
                                        }}
                                    >
                                        <option value=""></option>
                                        {dataClients.map((client) => (
                                            <option key={client?.id} value={client?.id}>
                                                {client?.libelle}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={6} md={6}>
                                <FormControl
                                    variant="outlined"
                                    sx={{ marginTop: 1 }}
                                    fullWidth
                                >
                                    <InputLabel
                                        htmlFor="operation"
                                        color="warning"
                                        shrink={CheckAttr(extractionDatas?.operation)}
                                    >
                                        Opération
                                    </InputLabel>
                                    <Select
                                        id="operation"
                                        native
                                        color="warning"
                                        value={extractionDatas?.operation}
                                        onChange={handleChange}
                                        label="Opération"
                                        inputProps={{
                                            name: "operation",
                                        }}
                                    >
                                        <option value=""></option>
                                        {dataOperations.map((operation) => (
                                            <option key={operation?.id} value={operation?.id}>
                                                {operation?.libelle}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl
                                    variant="outlined"
                                    sx={{ marginTop: 1 }}
                                    fullWidth
                                >
                                    <InputLabel
                                        htmlFor="type"
                                        color="warning"
                                        shrink={CheckAttr(extractionType)}
                                    >
                                        Type d'extraction
                                    </InputLabel>
                                    <Select
                                        id="type"
                                        native
                                        color="warning"
                                        value={extractionType}
                                        onChange={(e) => setExtractionTypes(e.target.value as ExtractionType)}
                                        label="Type d'extraction"
                                    >
                                        <option value={EXTRACTION_TYPE.CONSOLIDATION}>Consolidation</option>
                                        <option value={EXTRACTION_TYPE?.DETAIL_PAR_ARTICLE}>Détail par article</option>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {extractionType === EXTRACTION_TYPE?.CONSOLIDATION ? (
                                <Fragment>
                                    <Grid item xs={6} sm={6} md={6}>
                                        <TextField
                                            name="date_debut"
                                            label="Date debut"
                                            type="date"
                                            color="warning"
                                            fullWidth
                                            error={errorInput?.date_debut}
                                            value={extractionDatas?.date_debut}
                                            onChange={handleChange}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            sx={{ mt: 1 }}
                                        />
                                    </Grid>

                                    <Grid item xs={6} sm={6} md={6}>
                                        <TextField
                                            name="date_fin"
                                            label="Date fin"
                                            type="date"
                                            color="warning"
                                            fullWidth
                                            error={errorInput?.date_fin}
                                            value={extractionDatas?.date_fin}
                                            onChange={handleChange}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            sx={{ mt: 1 }}
                                        />
                                    </Grid>
                                </Fragment>
                            ) : (
                                <Fragment>
                                    <Grid item xs={6} sm={6} md={6}>
                                        <FormControl
                                            variant="outlined"
                                            sx={{ marginTop: 1 }}
                                            fullWidth
                                        >
                                            <InputLabel
                                                htmlFor="annee"
                                                color="warning"
                                                shrink={CheckAttr(extractionDatas?.annee)}
                                            >
                                                Année
                                            </InputLabel>
                                            <Select
                                                id="annee"
                                                native
                                                color="warning"
                                                error={errorInput?.annee}
                                                value={extractionDatas?.annee}
                                                onChange={handleChange}
                                                label="Année"
                                                inputProps={{
                                                    name: "annee",
                                                }}
                                            >
                                                <option value=""></option>
                                                {yearsToDisplayCommission.map((year) => (
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={6} sm={6} md={6}>
                                        <FormControl
                                            variant="outlined"
                                            sx={{ marginTop: 1 }}
                                            fullWidth
                                        >
                                            <InputLabel
                                                htmlFor="mois"
                                                color="warning"
                                                shrink={CheckAttr(extractionDatas?.mois)}
                                            >
                                                Mois
                                            </InputLabel>
                                            <Select
                                                id="mois"
                                                native
                                                color="warning"
                                                value={extractionDatas?.mois}
                                                onChange={handleChange}
                                                label="Mois"
                                                inputProps={{
                                                    name: "mois",
                                                }}
                                            >
                                                <option value=""></option>
                                                {AllMounthCorriger.map((month) => (
                                                    <option key={month?.id} value={month?.id}>
                                                        {month?.mounth}
                                                    </option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Fragment>
                            )}
                        </Grid>
                    </CardContent>

                    <CardActions>
                        <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
                            <Grid item xs={12} sm={12} md={12}>
                                <Box display="flex" justifyContent="center">
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        size="large"
                                        sx={{ textTransform: 'none' }}
                                        endIcon={<FileDownloadIcon />}
                                        onClick={() => handleExtract()}
                                    >
                                        Extraire
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardActions>
                </Card>
            </Container>

            {stackAlert}
            <div>
                <Backdrop
                    sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={loader}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </div>
        </>
    );
};

export default ExtractionCommission;